import { formatCatalogPrice, parseMinFromQuotaLabel } from '@/utils/catalogPricing';

const SECTION_TAG_MAP = [
  ['Профессиональное развитие', 'professionalDevelopment'],
  ['Экспертное сопровождение', 'expertSupport'],
  ['Оценка', 'tests'],
  ['Аналитика', 'analytics'],
  ['Развитие', 'development'],
];

const NON_DEVELOPMENT_SECTION_KEYS = new Set([
  'professionalDevelopment',
  'expertSupport',
  'tests',
  'analytics',
]);

const SLUG_SECTION_FALLBACK = {
  hr_ei_program: ['professionalDevelopment'],
  emotional_leadership_masterclass: ['expertSupport'],
};

const KNOWN_PRODUCT_SECTIONS = [
  {
    slugs: ['hr_ei_program'],
    nameIncludes: 'Программа для HR',
    sectionKeys: ['professionalDevelopment'],
  },
  {
    slugs: ['emotional_leadership_masterclass'],
    nameIncludes: 'Мастер-класс «Эмоциональное лидерство»',
    sectionKeys: ['expertSupport'],
  },
];

function resolveKnownProductSections(apiProduct) {
  const slug = apiProduct?.slug;
  const name = String(apiProduct?.name ?? '');

  return KNOWN_PRODUCT_SECTIONS.find((matcher) => (
    (matcher.slugs?.includes(slug))
    || (matcher.nameIncludes && name.includes(matcher.nameIncludes))
  ))?.sectionKeys ?? null;
}

function parsePriceRangeValue(priceRange) {
  if (priceRange == null || priceRange === '') {
    return null;
  }

  if (typeof priceRange === 'string') {
    try {
      return JSON.parse(priceRange);
    } catch {
      return null;
    }
  }

  return priceRange;
}

function normalizePriceRange(priceRange) {
  const parsedPriceRange = parsePriceRangeValue(priceRange);

  if (!parsedPriceRange) {
    return null;
  }

  if (Array.isArray(parsedPriceRange)) {
    return parsedPriceRange.reduce((acc, tier) => {
      if (tier == null || typeof tier !== 'object') {
        return acc;
      }

      const key = tier.to
        ?? tier.max
        ?? tier.max_count
        ?? tier.count
        ?? tier.quantity
        ?? tier.key
        ?? tier.employees;
      const price = tier.price ?? tier.value ?? tier.amount;

      if (key == null || price == null) {
        return acc;
      }

      acc[String(key)] = Number(price);

      return acc;
    }, {});
  }

  if (typeof parsedPriceRange === 'object') {
    return Object.fromEntries(
        Object.entries(parsedPriceRange).map(([key, value]) => {
          if (value != null && typeof value === 'object') {
            const nestedPrice = value.price ?? value.value ?? value.amount;

            return [String(key), Number(nestedPrice)];
          }

          return [String(key), Number(value)];
        }),
    );
  }

  return null;
}

export function buildQuotaOptionsFromPriceRange(priceRange) {
  const parsedPriceRange = parsePriceRangeValue(priceRange);

  if (!Array.isArray(parsedPriceRange) || !parsedPriceRange.length) {
    return null;
  }

  const options = parsedPriceRange
      .map((tier) => {
        if (tier == null || typeof tier !== 'object') {
          return null;
        }

        const max = tier.to
          ?? tier.max
          ?? tier.max_count
          ?? tier.count
          ?? tier.quantity
          ?? tier.key
          ?? tier.employees;
        const min = tier.from
          ?? tier.min
          ?? tier.min_count;
        const price = tier.price ?? tier.value ?? tier.amount;

        if (max == null) {
          return null;
        }

        const numericMax = Number(max);
        const numericMin = min != null ? Number(min) : null;
        const isLastTier = numericMax >= 10000;
        const label = numericMin != null
            ? (isLastTier
                ? `${numericMin.toLocaleString('ru-RU')}+`
                : `${numericMin}-${numericMax}`)
            : String(numericMax);

        return {
          label,
          value: numericMax,
          min: numericMin,
          price: price != null ? Number(price) : null,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.value - b.value);

  if (!options.length) {
    return null;
  }

  let previousMax = 0;

  return options.map((option, index) => {
    if (option.min != null) {
      previousMax = option.value;
      return option;
    }

    const parsedMin = parseMinFromQuotaLabel(option.label);
    const defaultFirstMin = option.value <= 9 ? 1 : 10;
    const min = parsedMin ?? (index === 0 ? defaultFirstMin : previousMax + 1);

    previousMax = option.value;

    return {
      ...option,
      min,
    };
  });
}

export function resolveSectionKeysFromTags(tags = []) {
  if (!Array.isArray(tags) || !tags.length) {
    return [];
  }

  const sectionKeys = SECTION_TAG_MAP
      .filter(([tag]) => tags.includes(tag))
      .map(([, sectionKey]) => sectionKey);

  if (sectionKeys.some((key) => NON_DEVELOPMENT_SECTION_KEYS.has(key))) {
    return sectionKeys.filter((key) => key !== 'development');
  }

  return sectionKeys;
}

export function resolveSectionKeysFromProduct(apiProduct) {
  const knownSections = resolveKnownProductSections(apiProduct);

  if (knownSections?.length) {
    return knownSections;
  }

  const slugSections = SLUG_SECTION_FALLBACK[apiProduct?.slug];

  if (slugSections?.length) {
    return slugSections;
  }

  return resolveSectionKeysFromTags(apiProduct?.tags);
}

export function mapApiProductToCatalogProduct(apiProduct) {
  const product = {
    title: apiProduct.name ?? '',
    description: '',
    orderProductKey: apiProduct.slug ?? String(apiProduct.id),
    unit: apiProduct.unit ?? '',
  };

  const priceRange = normalizePriceRange(apiProduct.price_range);
  const priceTierOptions = buildQuotaOptionsFromPriceRange(apiProduct.price_range);

  if (priceTierOptions?.length) {
    product.priceTierOptions = priceTierOptions;
  }

  if (priceRange && Object.keys(priceRange).length) {
    product.prices = priceRange;
  } else if (apiProduct.price != null && apiProduct.price !== '') {
    product.priceText = formatCatalogPrice(apiProduct.price);

    if (apiProduct.price_from) {
      product.pricePrefix = 'От';
    }
  }

  return product;
}

export function groupApiProductsBySection(apiProducts = []) {
  const grouped = {
    tests: [],
    analytics: [],
    development: [],
    professionalDevelopment: [],
    expertSupport: [],
  };

  apiProducts.forEach((apiProduct) => {
    const sectionKeys = resolveSectionKeysFromProduct(apiProduct);
    const catalogProduct = mapApiProductToCatalogProduct(apiProduct);

    sectionKeys.forEach((sectionKey) => {
      grouped[sectionKey].push(catalogProduct);
    });
  });

  return grouped;
}
